import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useDebouncedValue } from '@omnea-app/frontend/utils/hooks/useDebouncedValue'
import { throwIfApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { CUSTOM_SUPPLIER_ID } from '@omnea-app/products/productsFrontendClient'
import { ProductsHttpClient } from '@omnea-app/products/productsHttpClient'
import { SupplierOptionType, type ProductOptionType, type NewSupplier } from '../types'
import { sortOptionsByType } from '../utils'

export const createNewSupplierOption = (name: string): NewSupplier => {
  return { id: CUSTOM_SUPPLIER_ID, name, type: 'NEW_SUPPLIER', isPreferred: false, logoUrl: null }
}

/**
 * Returns an array of options or a single new supplier option if there are no options available.
 * The ListBoxWithAddNewOption component handles rendering of the "Add new supplier" option when there are options returned from the API.
 **/
const getOptions = (
  searchTerm: string,
  data: { suppliers: SupplierOptionType[]; products: ProductOptionType[] } | undefined
): (ProductOptionType | SupplierOptionType)[] | [NewSupplier] => {
  const suppliers = data?.suppliers ?? []
  const products = data?.products ?? []
  const options: (ProductOptionType | SupplierOptionType)[] = [...suppliers, ...products]

  // Only show new supplier option if we have a search term and no matches
  if (options.length === 0 && searchTerm.length > 0) {
    return [createNewSupplierOption(searchTerm)]
  }

  return options.sort(sortOptionsByType)
}

const SearchSuppliersAndProductsQueryKey = (name: string, includeGlobal: boolean, limit?: number) => [
  'SearchSuppliersAndProductsQueryKey',
  name,
  includeGlobal,
  limit,
]

const useQueryFn = ({
  name,
  includeGlobal,
  limit,
  includeSupplierTags,
}: {
  name: string
  includeGlobal: boolean
  limit?: number
  includeSupplierTags?: boolean
}) => {
  const { getToken } = useAuthenticatedUser()

  return async ({ signal }: { signal?: AbortSignal }) => {
    const token = await getToken()
    const apiClient = new ProductsHttpClient({ authorization: token })
    const { data, error } = await apiClient.GET('/search', {
      params: {
        query: {
          name,
          includeGlobal: includeGlobal ? 'true' : 'false',
          includeSupplierTags: includeSupplierTags ? 'true' : 'false',
          limit: limit !== undefined ? limit.toString() : undefined,
        },
      },
      signal,
    })
    throwIfApiError(error)

    const products = data.products ?? []
    const suppliers = data.suppliers ?? []

    return {
      products,
      suppliers,
    }
  }
}

export const useSuppliersAndProducts = ({
  name,
  includeGlobal,
  limit,
  enableOverride,
  includeSupplierTags,
}: {
  name: string
  includeGlobal: boolean
  limit?: number
  enableOverride?: boolean
  includeSupplierTags?: boolean
}) => {
  const debouncedSearchTerm = useDebouncedValue(name, { delay: 150 })
  const enabled = (debouncedSearchTerm.length > 0 || enableOverride) ?? false
  return useQuery({
    queryKey: SearchSuppliersAndProductsQueryKey(debouncedSearchTerm, includeGlobal, limit),
    queryFn: useQueryFn({ name: debouncedSearchTerm, includeGlobal, limit, includeSupplierTags }),
    enabled,
    // The endpoint will be invoked many times whilst a user searches. We only care about the current query result
    // The lines below ensure the the stale HTTP calls are aborted
    staleTime: 0,
    gcTime: 0,
  })
}

export const useSuppliersAndProductsSearch = ({
  includeGlobal,
  limit,
  enableOverride,
  includeSupplierTags,
}: {
  includeGlobal: boolean
  limit?: number
  enableOverride?: boolean
  includeSupplierTags?: boolean
}) => {
  const [searchTerm, setSearchTerm] = useState('%')
  const [options, setOptions] = useState<(ProductOptionType | SupplierOptionType)[] | [NewSupplier]>([])
  const [isManualLoading, setIsManualLoading] = useState(false)

  const { data, isLoading: isSearchLoading } = useSuppliersAndProducts({
    name: searchTerm,
    includeGlobal,
    limit,
    enableOverride,
    includeSupplierTags,
  })

  const search = useCallback(
    async (term: string) => {
      const trimmedTerm = term.trim()
      setSearchTerm(trimmedTerm)
      setIsManualLoading(true)

      if (!trimmedTerm) {
        setOptions(getOptions(trimmedTerm, { suppliers: [], products: [] }))
      }

      if (data) {
        setOptions(getOptions(trimmedTerm, data))
      }
      setIsManualLoading(false)
    },
    [data]
  )

  const isLoading = isManualLoading || isSearchLoading

  return {
    isLoading,
    search,
    options,
  }
}
